import { useState, useEffect, useCallback, useMemo } from 'react'
import { glorifyApiClient } from '../../../services/api/glorify'
import type { InvoiceSettings, UpdateInvoiceSettingsRequest } from '../../../services/api/glorify'
import { toSectionError, type SectionError } from './marketsTheme'
import { SectionErrorBanner } from './marketsShared'

// ─── Local editable form state — every field is a raw string, mirroring the
// wire shape (numeric-looking fields are parsed only right before submit,
// same NaN-proofing approach as OptionsCalculatorPage.tsx's num()). ─────────

interface SettingsFormState {
  business_name: string
  business_abn: string
  invoice_prefix: string
  next_invoice_sequence: string
  sequence_padding: string
  /** Entered as a whole percent (e.g. "10"), converted to a decimal string ("0.1000") on save. */
  gst_rate_percent: string
  business_address_line1: string
  business_address_line2: string
  business_city: string
  business_state: string
  business_postcode: string
  business_country: string
  business_email: string
  business_phone: string
  bank_account_name: string
  bank_bsb: string
  bank_account_number: string
  payment_terms_days: string
}

function formStateFromSettings(s: InvoiceSettings): SettingsFormState {
  const gstPercent = Number(s.default_gst_rate)
  return {
    business_name: s.business_name,
    business_abn: s.business_abn,
    invoice_prefix: s.invoice_prefix,
    next_invoice_sequence: String(s.next_invoice_sequence),
    sequence_padding: String(s.sequence_padding),
    gst_rate_percent: Number.isFinite(gstPercent) ? String(gstPercent * 100) : '10',
    business_address_line1: s.business_address_line1 ?? '',
    business_address_line2: s.business_address_line2 ?? '',
    business_city: s.business_city ?? '',
    business_state: s.business_state ?? '',
    business_postcode: s.business_postcode ?? '',
    business_country: s.business_country,
    business_email: s.business_email ?? '',
    business_phone: s.business_phone ?? '',
    bank_account_name: s.bank_account_name ?? '',
    bank_bsb: s.bank_bsb ?? '',
    bank_account_number: s.bank_account_number ?? '',
    payment_terms_days: String(s.payment_terms_days),
  }
}

function intOrFallback(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

function formStateToRequest(form: SettingsFormState): UpdateInvoiceSettingsRequest {
  const opt = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim())
  const gstPercent = Number(form.gst_rate_percent)
  return {
    business_name: form.business_name.trim(),
    business_abn: form.business_abn.trim(),
    invoice_prefix: form.invoice_prefix.trim(),
    next_invoice_sequence: intOrFallback(form.next_invoice_sequence, 1),
    sequence_padding: intOrFallback(form.sequence_padding, 4),
    default_gst_rate: Number.isFinite(gstPercent) ? (gstPercent / 100).toFixed(4) : '0.1000',
    business_address_line1: opt(form.business_address_line1),
    business_address_line2: opt(form.business_address_line2),
    business_city: opt(form.business_city),
    business_state: opt(form.business_state),
    business_postcode: opt(form.business_postcode),
    business_country: opt(form.business_country),
    business_email: opt(form.business_email),
    business_phone: opt(form.business_phone),
    bank_account_name: opt(form.bank_account_name),
    bank_bsb: opt(form.bank_bsb),
    bank_account_number: opt(form.bank_account_number),
    payment_terms_days: intOrFallback(form.payment_terms_days, 14),
  }
}

/** e.g. prefix "INV-", sequence 7, padding 4 -> "INV-0007". */
function previewInvoiceNumber(prefix: string, sequence: string, padding: string): string {
  const seq = intOrFallback(sequence, 1)
  const pad = Math.max(0, intOrFallback(padding, 4))
  return `${prefix}${String(seq).padStart(pad, '0')}`
}

export default function InvoiceSettingsPage(): React.JSX.Element {
  const [form, setForm] = useState<SettingsFormState | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<SectionError | null>(null)
  const [saveError, setSaveError] = useState<SectionError | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const fetchSettings = useCallback(async (): Promise<void> => {
    setLoadError(null)
    try {
      const settings = await glorifyApiClient.getInvoiceSettings()
      setForm(formStateFromSettings(settings))
    } catch (err) {
      setLoadError(toSectionError(err, 'Failed to load invoice settings'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchSettings()
  }, [fetchSettings])

  const set = (patch: Partial<SettingsFormState>): void => setForm((prev) => (prev ? { ...prev, ...patch } : prev))

  const preview = useMemo(
    () => (form ? previewInvoiceNumber(form.invoice_prefix, form.next_invoice_sequence, form.sequence_padding) : ''),
    [form]
  )

  const handleSave = async (): Promise<void> => {
    if (!form) return
    setIsSaving(true)
    setSaveError(null)
    setSavedAt(null)
    try {
      const updated = await glorifyApiClient.updateInvoiceSettings(formStateToRequest(form))
      setForm(formStateFromSettings(updated))
      setSavedAt(Date.now())
    } catch (err) {
      setSaveError(toSectionError(err, 'Failed to save invoice settings'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <div className="gd-page-header">
        <h1>Invoice Settings</h1>
        <p>Business details, invoice numbering, GST rate, and bank details used on every generated invoice PDF.</p>
      </div>

      <SectionErrorBanner error={loadError} />

      {isLoading || !form ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading settings" />
        </div>
      ) : (
        <>
          <SectionErrorBanner error={saveError} />
          {savedAt !== null && !saveError && (
            <div className="gd-banner gd-banner--info" style={{ marginBottom: 16 }}>
              <span className="material-icons" aria-hidden="true">check_circle</span>
              <p>Settings saved.</p>
            </div>
          )}

          <div className="gd-section">
            <h2 className="gd-section__title">Business Details</h2>
            <div className="gd-setup-row">
              <div className="gd-field">
                <label htmlFor="gd-settings-business-name">Business name</label>
                <input id="gd-settings-business-name" type="text" value={form.business_name} onChange={(e) => set({ business_name: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-abn">ABN</label>
                <input id="gd-settings-abn" type="text" value={form.business_abn} onChange={(e) => set({ business_abn: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-email">Business email</label>
                <input id="gd-settings-email" type="email" value={form.business_email} onChange={(e) => set({ business_email: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-phone">Business phone</label>
                <input id="gd-settings-phone" type="text" value={form.business_phone} onChange={(e) => set({ business_phone: e.target.value })} />
              </div>
            </div>
            <div className="gd-setup-row">
              <div className="gd-field" style={{ flex: 1, minWidth: 200 }}>
                <label htmlFor="gd-settings-addr1">Address line 1</label>
                <input id="gd-settings-addr1" type="text" value={form.business_address_line1} onChange={(e) => set({ business_address_line1: e.target.value })} />
              </div>
              <div className="gd-field" style={{ flex: 1, minWidth: 200 }}>
                <label htmlFor="gd-settings-addr2">Address line 2</label>
                <input id="gd-settings-addr2" type="text" value={form.business_address_line2} onChange={(e) => set({ business_address_line2: e.target.value })} />
              </div>
            </div>
            <div className="gd-setup-row">
              <div className="gd-field">
                <label htmlFor="gd-settings-city">City</label>
                <input id="gd-settings-city" type="text" value={form.business_city} onChange={(e) => set({ business_city: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-state">State</label>
                <input id="gd-settings-state" type="text" value={form.business_state} onChange={(e) => set({ business_state: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-postcode">Postcode</label>
                <input id="gd-settings-postcode" type="text" value={form.business_postcode} onChange={(e) => set({ business_postcode: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-country">Country</label>
                <input id="gd-settings-country" type="text" value={form.business_country} onChange={(e) => set({ business_country: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="gd-section">
            <h2 className="gd-section__title">Invoice Numbering &amp; GST</h2>
            <div className="gd-setup-row">
              <div className="gd-field">
                <label htmlFor="gd-settings-prefix">Invoice prefix</label>
                <input id="gd-settings-prefix" type="text" value={form.invoice_prefix} onChange={(e) => set({ invoice_prefix: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-next-seq">Next sequence number</label>
                <input id="gd-settings-next-seq" type="number" min="1" step="1" value={form.next_invoice_sequence} onChange={(e) => set({ next_invoice_sequence: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-padding">Sequence zero-padding</label>
                <input id="gd-settings-padding" type="number" min="0" step="1" value={form.sequence_padding} onChange={(e) => set({ sequence_padding: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-gst">Default GST rate (%)</label>
                <input id="gd-settings-gst" type="number" min="0" max="100" step="0.01" value={form.gst_rate_percent} onChange={(e) => set({ gst_rate_percent: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-terms">Payment terms (days)</label>
                <input id="gd-settings-terms" type="number" min="0" step="1" value={form.payment_terms_days} onChange={(e) => set({ payment_terms_days: e.target.value })} />
              </div>
            </div>
            <p className="gd-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
              Next invoice number will be <b style={{ color: 'var(--md-sys-color-on-surface)' }}>{preview}</b>
            </p>
          </div>

          <div className="gd-section">
            <h2 className="gd-section__title">Bank Details</h2>
            <p className="gd-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)', marginTop: -8 }}>
              Shown on the invoice PDF footer for bank-transfer payment.
            </p>
            <div className="gd-setup-row">
              <div className="gd-field">
                <label htmlFor="gd-settings-bank-name">Account name</label>
                <input id="gd-settings-bank-name" type="text" value={form.bank_account_name} onChange={(e) => set({ bank_account_name: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-bsb">BSB</label>
                <input id="gd-settings-bsb" type="text" value={form.bank_bsb} onChange={(e) => set({ bank_bsb: e.target.value })} />
              </div>
              <div className="gd-field">
                <label htmlFor="gd-settings-account-number">Account number</label>
                <input id="gd-settings-account-number" type="text" value={form.bank_account_number} onChange={(e) => set({ bank_account_number: e.target.value })} />
              </div>
            </div>
          </div>

          <button className="gd-btn gd-btn--filled" disabled={isSaving} onClick={() => void handleSave()}>
            {isSaving ? 'Saving…' : 'Save settings'}
          </button>
        </>
      )}
    </>
  )
}
